import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { X, Mail, MessageCircle, AlertCircle, ArrowLeft, Lock, Eye, EyeOff } from 'lucide-react';
import { fetchCaptcha, verifyCaptchaThunk, verifyPasswordLoginThunk, clearError, verifyGoogleLoginThunk } from '../store/userSlice';
import { useTranslation } from 'react-i18next';
import { useGoogleAuth } from './auth/hooks/useGoogleAuth';

// 谷歌图标组件（Google 登录已隐藏，保留以便恢复）
// eslint-disable-next-line no-unused-vars
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

export default function LoginModal({ isOpen, onClose, onLogin }) {
  const dispatch = useDispatch();
  const { loading: reduxLoading, error: reduxError } = useSelector(state => state.user);
  const { t } = useTranslation();
  
  // 登录方式：'code' | 'password'
  const [loginMode, setLoginMode] = useState('code');
  // 屏幕状态：'email' | 'verification' (仅验证码模式使用)
  const [currentScreen, setCurrentScreen] = useState('email');
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    verificationCode: ['', '', '', '', '', ''] // 改为数组存储6位验证码
  });
  const [showPassword, setShowPassword] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [localError, setLocalError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeLoginMethod, setActiveLoginMethod] = useState('');
  const [loginPending, setLoginPending] = useState(false);
  
  // 合并加载状态
  const loading = reduxLoading || loginPending;

  // 处理表单输入
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // 清除错误信息
    if (localError) setLocalError('');
    if (reduxError) dispatch(clearError());
  };

  // 处理验证码输入
  const handleCodeChange = (index, value) => {
    // 只允许数字
    if (!/^\d*$/.test(value)) return;
    
    const newCode = [...formData.verificationCode];
    newCode[index] = value;
    
    setFormData(prev => ({
      ...prev,
      verificationCode: newCode
    }));
    
    // 清除错误信息
    if (localError) setLocalError('');
    if (reduxError) dispatch(clearError());
    
    // 自动跳转到下一个输入框
    if (value && index < 5) {
      const nextInput = document.getElementById(`code-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  // 处理退格键
  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !formData.verificationCode[index] && index > 0) {
      const prevInput = document.getElementById(`code-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  // 验证邮箱格式
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // 使用 Google 认证 Hook
  const { 
    isApiLoaded,
    handleGoogleLogin,
    apiError,
    reloadGoogleApi
  } = useGoogleAuth({
    setIsLoading: setLoginPending,
    setErrorMessage: (error) => {
      if (activeLoginMethod === 'google' && loginPending) {
        setLocalError(error);
        setLoginPending(false);
      }
    },
    onLoginSuccess: async (userInfo) => {
      setLocalError('');
      setLoginPending(false);
      
      // 使用Redux thunk处理Google登录
      const result = await dispatch(verifyGoogleLoginThunk({ credential: userInfo.credential || userInfo.email }));
      
      if (verifyGoogleLoginThunk.fulfilled.match(result)) {
        setSuccess('Google登录成功！');
        
        // 调用父组件的登录回调
        if (onLogin) {
          onLogin();
        }
        
        // 延迟关闭弹窗
        setTimeout(() => {
          handleClose();
        }, 1000);
      } else {
        setLocalError(result.payload || 'Google登录失败');
      }
    },
    shouldLoad: false
  });

  // 监听 API 加载完成
  useEffect(() => {
    if (isApiLoaded && activeLoginMethod === 'google' && loginPending) {
      handleGoogleLogin();
    }
  }, [isApiLoaded, activeLoginMethod, loginPending, handleGoogleLogin]);

  // 处理邮箱提交（第一屏）
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !isValidEmail(formData.email)) {
      setLocalError(t('loginModal.errors.invalidEmail'));
      return;
    }

    setLocalError('');
    dispatch(clearError());
    
    try {
      // 使用Redux thunk发送验证码
      const result = await dispatch(fetchCaptcha(formData.email));
      
      if (fetchCaptcha.fulfilled.match(result)) {
        setCodeSent(true);
        setCountdown(60);
        setSuccess(t('loginModal.success.codeSent'));
        
        // 切换到验证码屏幕
        setCurrentScreen('verification');
        
        // 倒计时
        const timer = setInterval(() => {
          setCountdown(prev => {
            if (prev <= 1) {
              clearInterval(timer);
              setCodeSent(false);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        // 处理发送失败的情况
        setLocalError(result.payload || t('loginModal.errors.sendCodeFailed'));
      }
      
    } catch (err) {
      setLocalError(err.message || t('loginModal.errors.sendCodeFailed'));
    }
  };

  // 重新发送验证码
  const handleResendCode = async () => {
    setLocalError('');
    dispatch(clearError());
    
    try {
      // 使用Redux thunk重新发送验证码
      const result = await dispatch(fetchCaptcha(formData.email));
      
      if (fetchCaptcha.fulfilled.match(result)) {
        setCodeSent(true);
        setCountdown(60);
        setSuccess(t('loginModal.success.codeResent'));
        
        // 倒计时
        const timer = setInterval(() => {
          setCountdown(prev => {
            if (prev <= 1) {
              clearInterval(timer);
              setCodeSent(false);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        setLocalError(result.payload || t('loginModal.errors.sendCodeFailed'));
      }
      
    } catch (err) {
      setLocalError(err.message || t('loginModal.errors.sendCodeFailed'));
    }
  };

  // 处理验证码提交（第二屏）
  const handleVerificationSubmit = async (e) => {
    e.preventDefault();
    
    const code = formData.verificationCode.join('');
    
    if (!code || code.length !== 6) {
      setLocalError(t('loginModal.errors.incompleteCode'));
      return;
    }

    setLocalError('');
    dispatch(clearError());

    try {
      // 使用Redux thunk验证验证码
      const result = await dispatch(verifyCaptchaThunk({
        phoneNumbers: formData.email,
        smsCode: code
      }));
      
      if (verifyCaptchaThunk.fulfilled.match(result)) {
        setSuccess(t('loginModal.success.loginSuccess'));
        
        // 调用父组件的登录回调（不需要传递用户数据，Redux已管理）
        if (onLogin) {
          onLogin();
        }
        
        // 延迟关闭弹窗
        setTimeout(() => {
          handleClose();
        }, 1000);
      } else {
        setLocalError(result.payload || t('loginModal.errors.verificationFailed'));
      }
      
    } catch (err) {
      setLocalError(err.message || t('loginModal.errors.loginFailed'));
    }
  };

  // 处理账号密码登录提交
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email) {
      setLocalError(t('loginModal.errors.emailRequired'));
      return;
    }

    if (!formData.password) {
      setLocalError(t('loginModal.errors.passwordRequired'));
      return;
    }

    setLocalError('');
    dispatch(clearError());

    try {
      // 调用账号密码登录API
      const result = await dispatch(verifyPasswordLoginThunk({
        phoneNumbers: formData.email,
        password: formData.password
      }));

      if (result.type === 'user/verifyPasswordLogin/fulfilled') {
        // 登录成功
        setSuccess(t('loginModal.success.loginSuccess'));
        
        if (onLogin) {
          onLogin();
        }
        
        setTimeout(() => {
          handleClose();
        }, 1000);
      } else {
        // 登录失败
        setLocalError(result.payload || t('loginModal.errors.loginFailed'));
      }
    } catch (err) {
      setLocalError(err.message || t('loginModal.errors.loginFailed'));
    }
  };

  // 返回邮箱输入屏幕
  const handleBackToEmail = () => {
    setCurrentScreen('email');
    setLocalError('');
    setSuccess('');
    dispatch(clearError());
    setFormData(prev => ({ ...prev, verificationCode: ['', '', '', '', '', ''] }));
  };

  // 处理 Google 登录点击（已隐藏，保留以便恢复）
  // eslint-disable-next-line no-unused-vars
  const handleGoogleLoginClick = () => {
    setActiveLoginMethod('google');
    setLoginPending(true);
    
    if (apiError) {
      setLocalError(apiError);
      setLoginPending(false);
      return;
    }
    
    if (isApiLoaded) {
      handleGoogleLogin();
    }
  };

  // 重试登录（已隐藏，保留以便恢复）
  // eslint-disable-next-line no-unused-vars
  const handleRetryGoogleLogin = () => {
    setLocalError('');
    setLoginPending(true);
    reloadGoogleApi();
  };

  // 切换登录模式
  const handleModeSwitch = (mode) => {
    setLoginMode(mode);
    setCurrentScreen('email');
    setLocalError('');
    setSuccess('');
    setShowPassword(false);
    dispatch(clearError());
    setFormData({
      email: '',
      password: '',
      verificationCode: ['', '', '', '', '', '']
    });
  };

  // 关闭弹窗并重置状态
  const handleClose = () => {
    onClose();
    setLoginMode('code');
    setCurrentScreen('email');
    setFormData({
      email: '',
      password: '',
      verificationCode: ['', '', '', '', '', '']
    });
    setCodeSent(false);
    setCountdown(0);
    setLocalError('');
    setSuccess('');
    setShowPassword(false);
    dispatch(clearError());
  };

  // 获取当前错误信息（优先显示本地错误）
  const currentError = localError || reduxError;

  // 如果弹窗关闭，不渲染
  if (!isOpen) return null;

  return createPortal(
    <>
      {/* 背景遮罩 */}
      <div
        className="fixed inset-0 z-[9998] bg-black/50"
        onClick={handleClose}
      />

      {/* 弹窗居中容器（独立 fixed 层，可滚动） */}
      <div className="site-dialog fixed inset-0 z-[9999] overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="relative bg-white rounded-2xl shadow-panel w-full max-w-md transform transition-all duration-300" onClick={(e) => e.stopPropagation()}>
        
        {/* 头部装饰 */}
        <div className="bg-brand p-6 text-white relative rounded-t-2xl">
          {/* 返回按钮（仅在验证码屏幕显示） */}
          {loginMode === 'code' && currentScreen === 'verification' && (
            <button
              onClick={handleBackToEmail}
              className="absolute top-4 left-4 w-8 h-8 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 flex items-center justify-center transition-all duration-300"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}
          
          <button
            onClick={handleClose}
            aria-label="关闭登录窗口"
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 flex items-center justify-center transition-all duration-300"
          >
            <X className="w-4 h-4" />
          </button>
          
          <div className="text-center">
            <div className="flex justify-center mx-auto mb-4">
              <img 
                src="/images/logo.jpg" 
                alt="网站logo" 
                className="w-16 h-16 object-contain rounded-full border-4 border-white"
                onError={(e) => {
                  // 如果logo加载失败，显示默认图标
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center hidden">
                {loginMode === 'password' ? (
                  <Lock className="w-8 h-8" />
                ) : currentScreen === 'email' ? (
                  <Mail className="w-8 h-8" />
                ) : (
                  <MessageCircle className="w-8 h-8" />
                )}
              </div>
            </div>
            <h2 className="text-2xl font-bold">
              {loginMode === 'code' && currentScreen === 'verification' 
                ? t('loginModal.title.verification') 
                : t('loginModal.title.login')}
            </h2>
            <p className="text-white/90 mt-2">
              {loginMode === 'code' && currentScreen === 'verification' 
                ? t('loginModal.subtitle.verification', { 
                    email: formData.email.replace(/(.{3}).*(@.*)/, '$1***$2') 
                  })
                : loginMode === 'password'
                ? t('loginModal.subtitle.passwordLogin')
                : t('loginModal.subtitle.login')
              }
            </p>
          </div>
        </div>


        {/* 表单内容 */}
        <div className="p-8">
          {/* 错误/成功信息 */}
          {currentError && (
            <div className="mb-4 p-3 bg-section border border-line rounded-lg flex items-center gap-2 text-ink">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm">{currentError}</span>
            </div>
          )}
          
          {success && (
            <div className="mb-4 p-3 bg-section border border-line rounded-lg flex items-center gap-2 text-ink">
              <MessageCircle className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm">{success}</span>
            </div>
          )}

          {/* 账号密码登录屏幕 */}
          {loginMode === 'password' && (
            <>
              <form onSubmit={handlePasswordSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-ink mb-2">
                    {t('loginModal.form.emailLabel')}
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted" />
                    <input
                      type="text"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder={t('loginModal.form.emailPlaceholder')}
                      className="w-full pl-12 pr-4 py-3 border border-line rounded-xl focus:border-black focus:outline-none transition-colors duration-300"
                      required
                      disabled={loading}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-ink mb-2">
                    {t('loginModal.form.passwordLabel')}
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder={t('loginModal.form.passwordPlaceholder')}
                      className="w-full pl-12 pr-12 py-3 border border-line rounded-xl focus:border-black focus:outline-none transition-colors duration-300"
                      required
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-muted hover:text-muted transition-colors duration-300"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-brand text-white py-4 rounded-xl font-semibold text-lg hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center gap-2 border border-brand"
                >
                  {loading ? (
                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    t('loginModal.form.login')
                  )}
                </button>
              </form>

              {/* Google 登录已隐藏（保留代码以便恢复）
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-line" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-muted">{t('loginModal.divider.or')}</span>
                </div>
              </div>

              <button
                onClick={handleGoogleLoginClick}
                disabled={loading}
                className="w-full bg-white border border-line text-ink py-4 rounded-xl font-semibold text-lg hover:bg-canvas hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center gap-3"
              >
                {loading && activeLoginMethod === 'google' ? (
                  <span className="flex items-center">
                    <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                    <span className="ml-2">{isApiLoaded ? t('loginModal.form.processing') : t('loginModal.form.loading')}</span>
                  </span>
                ) : (
                  <>
                    <GoogleIcon />
                    {t('loginModal.form.googleLogin')}
                  </>
                )}
              </button>
              */}

              {/* 切换到验证码登录 */}
              <div className="text-center mt-6 pt-4 border-t border-line">
                <button
                  onClick={() => handleModeSwitch('code')}
                  className="text-sm text-muted hover:text-ink transition-all duration-300 inline-flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-section"
                >
                  <MessageCircle className="w-4 h-4" />
                  <span>{t('loginModal.switch.toCode')}</span>
                </button>
              </div>
            </>
          )}

          {/* 邮箱输入屏幕（验证码模式） */}
          {loginMode === 'code' && currentScreen === 'email' && (
            <>
              <form onSubmit={handleEmailSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-ink mb-2">
                    {t('loginModal.form.emailLabel')}
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder={t('loginModal.form.emailPlaceholder')}
                      className="w-full pl-12 pr-4 py-3 border border-line rounded-xl focus:border-black focus:outline-none transition-colors duration-300"
                      required
                      disabled={loading}
                    />
                  </div>
                  <p className="text-xs text-muted mt-2">
                    {t('loginModal.form.emailHint')}
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-brand text-white py-4 rounded-xl font-semibold text-lg hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center gap-2 border border-brand"
                >
                  {loading ? (
                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    t('loginModal.form.sendCode')
                  )}
                </button>
              </form>

              {/* Google 登录已隐藏（保留代码以便恢复）
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-line" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-muted">{t('loginModal.divider.or')}</span>
                </div>
              </div>

              <button
                onClick={handleGoogleLoginClick}
                disabled={loading}
                className="w-full bg-white border border-line text-ink py-4 rounded-xl font-semibold text-lg hover:bg-canvas hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center gap-3"
              >
                {loading && activeLoginMethod === 'google' ? (
                  <span className="flex items-center">
                    <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                    <span className="ml-2">{isApiLoaded ? '正在处理...' : '加载中...'}</span>
                  </span>
                ) : (
                  <>
                    <GoogleIcon />
                    {t('loginModal.form.googleLogin')}
                  </>
                )}
              </button>

              {localError && activeLoginMethod === 'google' && (
                <div className="mt-4 p-4 bg-section text-ink rounded-lg text-sm">
                  <div className="flex items-center justify-between">
                    <div>{localError}</div>
                    <button
                      onClick={handleRetryGoogleLogin}
                      className="ml-3 px-3 py-1 bg-gray-200 text-ink rounded text-sm font-medium hover:bg-gray-300"
                    >
                      重试
                    </button>
                  </div>
                </div>
              )}
              */}

              {/* 切换到账号密码登录 */}
              <div className="text-center mt-6 pt-4 border-t border-line">
                <button
                  onClick={() => handleModeSwitch('password')}
                  className="text-sm text-muted hover:text-ink transition-all duration-300 inline-flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-section"
                >
                  <Lock className="w-4 h-4" />
                  <span>{t('loginModal.switch.toPassword')}</span>
                </button>
              </div>
            </>
          )}

          {/* 验证码输入屏幕（验证码模式） */}
          {loginMode === 'code' && currentScreen === 'verification' && (
            <>
              <form onSubmit={handleVerificationSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-ink mb-4 text-center">
                    {t('loginModal.form.verificationLabel')}
                  </label>
                  
                  {/* 6个验证码输入框 */}
                  <div className="flex justify-center gap-3 mb-6">
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                      <input
                        key={index}
                        id={`code-${index}`}
                        type="text"
                        maxLength="1"
                        value={formData.verificationCode[index]}
                        onChange={(e) => handleCodeChange(index, e.target.value)}
                        onKeyDown={(e) => handleCodeKeyDown(index, e)}
                        className="w-9 sm:w-12 h-12 sm:h-14 text-center text-2xl font-bold border border-line rounded-xl focus:border-black focus:outline-none transition-colors duration-300"
                        disabled={loading}
                        autoFocus={index === 0}
                      />
                    ))}
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <p className="text-sm text-muted">
                      {t('loginModal.form.noCode')}
                    </p>
                    <button
                      type="button"
                      onClick={handleResendCode}
                      disabled={loading || codeSent}
                      className="text-sm text-ink hover:text-ink disabled:text-muted disabled:cursor-not-allowed font-medium"
                    >
                      {codeSent ? t('loginModal.form.resendCountdown', { countdown }) : t('loginModal.form.resend')}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-brand text-white py-4 rounded-xl font-semibold text-lg hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center gap-2 border border-brand"
                >
                  {loading ? (
                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    t('loginModal.form.login')
                  )}
                </button>
              </form>
            </>
          )}

          {/* 登录说明 */}
          <div className="text-center mt-6 pt-6 border-t border-line">
            <p className="text-sm text-muted leading-relaxed">
              {t('loginModal.footer.agreement')}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-brand mx-1">{t('loginModal.footer.terms')}</a>
              {t('loginModal.footer.and')}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-brand mx-1">{t('loginModal.footer.privacy')}</a>
            </p>
          </div>
        </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
} 